// src/app/pages/management/maintain-account/tabs/tab-uploads.component.ts

import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TranslateService } from '../../../../services/translate.service';
import { UploadItem } from '../../../../models/user.model';
import { DialogModule } from 'primeng/dialog'; // Import DialogModule
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'; // Import DomSanitizer and SafeResourceUrl

import { Pagination } from '../../../../services/pagination.abstruct';

@Component({
  selector: 'app-tab-uploads',
  standalone: true,
  imports: [
    CommonModule,FormsModule, TableModule, TagModule, ButtonModule, TooltipModule, DialogModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './tab-uploads.component.html',
  styleUrls: ['./tab-uploads.component.scss']
})
export class TabUploadsComponent extends Pagination implements OnInit {
  @Input() uploads: UploadItem[]= [];
  @Input() uploadsLoading!: boolean;
  @Input() uploadsError!: boolean;

  @ViewChild('dt') dt!: Table;
  
  globalFilter: string = '';
  globalFilterFields: string[] = [];

  // New properties for the file viewer dialog
  displayDialog: boolean = false;
  fileViewerUrl: string | null = null;
  safeFileViewerUrl: SafeResourceUrl | null = null; // For iframe src binding
  fileContentType: 'pdf' | 'image' | 'unsupported' | null = null;
  dialogHeader: string = '';

  constructor(
    public translateServ: TranslateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private sanitizer: DomSanitizer // Inject DomSanitizer
  ) { super();}

  ngOnInit(): void 
  {
    if (this.uploads?.length > 0) 
    {
      this.globalFilterFields = Object.keys(this.uploads[0]);
    }
  }

  getUploadSeverity(isActive: number): string {
    return isActive === 1 ? 'success' : 'danger';
  }

  editUpload(upload: UploadItem): void {
    console.log('Attempting to edit Upload record with RecID:', upload.RecID);
    this.messageService.add({
      severity: 'info',
      summary: this.translateServ.dictionary['EDIT_ACTION'] || 'Edit Action',
      detail: `${this.translateServ.dictionary['EDITING']} ${upload.Code} - ${upload.OriginalFileName} (RecID: ${upload.RecID})`,
      life: 3000
    });
    // Placeholder for actual edit dialog logic
  }

  toggleUploadStatus(upload: UploadItem): void {
    const newStatus = upload.IsActive === 1 ? 0 : 1;
    const actionText = newStatus === 1 ?
      (this.translateServ.dictionary['ACTIVATE'] || 'activate') :
      (this.translateServ.dictionary['DEACTIVATE'] || 'deactivate');
    const statusText = newStatus === 1 ?
      (this.translateServ.dictionary['ACTIVE_STATUS'] || 'Active') :
      (this.translateServ.dictionary['INACTIVE_STATUS'] || 'Inactive');

    const uploadIdentifier = `${upload.Code} - ${upload.OriginalFileName}`;

    this.confirmationService.confirm({
      message: `${this.translateServ.dictionary['CONFIRM_CHANGE_STATUS'] || 'Are you sure you want to'} ${actionText} ${uploadIdentifier}?`,
      header: this.translateServ.dictionary['CONFIRM_ACTION'] || 'Confirm Action',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateServ.dictionary['YES'] || 'Yes',
      rejectLabel: this.translateServ.dictionary['NO'] || 'No',
      accept: () => {
        console.log(`Toggling status of ${uploadIdentifier} (RecID: ${upload.RecID}) to ${newStatus}`);

        const index = this.uploads?.findIndex(item => item.RecID === upload.RecID);
        if (this.uploads && index !== undefined && index > -1) {
          this.uploads[index].IsActive = newStatus;
          this.messageService.add({
            severity: 'success',
            summary: this.translateServ.dictionary['STATUS_UPDATED'] || 'Status Updated',
            detail: `${uploadIdentifier} is now ${statusText}.`,
            life: 3000
          });
          // Call your backend service here to update the status
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateServ.dictionary['ERROR'] || 'Error',
            detail: this.translateServ.dictionary['RECORD_NOT_FOUND'] || 'Upload record not found in local data.',
            life: 5000
          });
        }
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled',
          detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Action cancelled.',
          life: 3000
        });
      }
    });
  }

  /**
   * Opens the file in a PrimeNG dialog. Supports PDF and common image types.
   */
  viewUploadFile(upload: UploadItem): void {
    if (!upload.URL) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateServ.dictionary['VIEW_FILE_ERROR'] || 'Cannot View File',
        detail: this.translateServ.dictionary['NO_URL_PROVIDED'] || 'No direct URL is available for this upload.',
        life: 5000
      });
      return;
    }

    this.fileViewerUrl = upload.URL;
    this.safeFileViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(upload.URL);
    this.dialogHeader = upload.OriginalFileName || this.translateServ.dictionary['FILE_VIEWER'] || 'File Viewer';

    const fileExtension = upload.OriginalFileName ?
                          upload.OriginalFileName.split('.').pop()?.toLowerCase() :
                          null;

    if (fileExtension) {
      switch (fileExtension) {
        case 'pdf':
          this.fileContentType = 'pdf';
          break;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'webp':
        case 'svg':
          this.fileContentType = 'image';
          break;
        default:
          this.fileContentType = 'unsupported';
          break;
      }
    } else {
      // If no extension, try to infer from DocType, or default to unsupported
      if (upload.DocType && upload.DocType.toLowerCase().includes('pdf')) {
        this.fileContentType = 'pdf';
      } else if (upload.DocType && (upload.DocType.toLowerCase().includes('image') || upload.DocType.toLowerCase().includes('img'))) {
        this.fileContentType = 'image';
      } else {
        this.fileContentType = 'unsupported';
      }
    }

    this.displayDialog = true;
  }

  /**
   * Resets dialog state when it is hidden.
   */
  onDialogHide(): void {
    this.fileViewerUrl = null;
    this.safeFileViewerUrl = null;
    this.fileContentType = null;
    this.dialogHeader = '';
  }

  createNewRecord(): void 
  {
    console.log('Create new record clicked');
    this.messageService.add({
      severity: 'info',
      summary: 'Create',
      detail: 'Create new record triggered',
      life: 3000
    });
}

  applyGlobalFilter(): void 
  {
    this.dt.filterGlobal(this.globalFilter, 'contains');
  }
}